import {
  ArrowBackIosNew,
  ArrowDownward,
  ArrowForwardIos,
  ArrowUpward,
} from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { COLORS } from "../../assets/theme";
import {
  IAction,
  IField,
  IPageable,
  IPageInput,
  ISort,
  Params,
} from "../../types/table/interfaces.type";
import DataTableBody from "./DataTableBody";
import DataCardList from "./DataCardList";
import CustomCheckbox from "../CustomCheckbox";
import { getObjectDiffMask } from "../../utils/diffUtils";

type FetchFuncType<T> =
  | ((params: Params<T>) => Promise<IPageable<T>>)
  | ((id: string) => Promise<T>);

function callFetchFunc<T>(
  fetchFunc: FetchFuncType<T>,
  params: Params<T>,
  id?: string
): any {
  if (id && fetchFunc.length === 1) {
    return (fetchFunc as (id: string) => any)(id);
  }
  return (fetchFunc as (params: Params<T>) => any)(params);
}

interface IPage extends IPageInput {
  totalPages: number;
  totalElements: number;
}

interface IPaginationRowProps {
  pageInput: IPageInput;
  setPageInput: (input: IPageInput | ((old: IPageInput) => IPageInput)) => void;
  page: IPage;
  paginationSummary?: ReactNode;
}

interface IPaginatedTableProps<T> {
  fields: IField<T>[];
  filters: Params<T>;
  fetchFunc: FetchFuncType<T>;
  actions: IAction<T>[];
  renderActions?: (row: T) => React.ReactElement[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  /** Clicking anywhere on a row (not on its buttons) opens it. */
  onRowClick?: (row: T) => void;
  sortableFields?: (keyof T)[];
  highlightDiff?: boolean;
  clientSidePagination?: boolean;
  refreshSignal?: any;
  loading?: boolean;
  paginationSummary?: ReactNode;
}

function PaginatedTable<T>({
  fields,
  filters,
  fetchFunc,
  actions,
  checkboxSelect,
  onRowSelect,
  isRowSelected,
  onRowClick,
  renderActions,
  shadeRow,
  shadeBg,
  shadeColor,
  sortableFields,
  highlightDiff = false,
  clientSidePagination = false,
  refreshSignal,
  loading: externalLoading = false,
  paginationSummary,
}: IPaginatedTableProps<T>) {
  const theme = useTheme();
  // Below 600px a wide table only scrolls sideways; rows become cards instead.
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [sort, setSort] = useState<ISort<T>>({});
  const initialPage =
    typeof (filters as any)?.page === "number" && Number.isFinite((filters as any).page)
      ? Math.max(0, (filters as any).page)
      : 0;
  const initialSize =
    typeof (filters as any)?.size === "number" && Number.isFinite((filters as any).size)
      ? Math.max(1, (filters as any).size)
      : 10;

  const [pageInput, setPageInput] = useState<IPageInput>({
    page: initialPage,
    size: initialSize,
  });

  // A new search or filter starts again from the first page.
  const filtersKey = JSON.stringify({
    ...filters,
    page: undefined,
    size: undefined,
  });
  const lastFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (lastFiltersKey.current === filtersKey) return;
    lastFiltersKey.current = filtersKey;
    setPageInput((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [filtersKey]);

  useEffect(() => {
    const nextPage =
      typeof (filters as any)?.page === "number" && Number.isFinite((filters as any).page)
        ? Math.max(0, (filters as any).page)
        : 0;
    const nextSize =
      typeof (filters as any)?.size === "number" && Number.isFinite((filters as any).size)
        ? Math.max(1, (filters as any).size)
        : pageInput.size;
    if (nextPage !== pageInput.page || nextSize !== pageInput.size) {
      setPageInput({ page: nextPage, size: nextSize });
    }
  }, [(filters as any)?.page, (filters as any)?.size]);

  const queryParams: Params<T> = {
    ...filters,
    page: clientSidePagination ? 0 : pageInput.page,
    size: clientSidePagination ? 1000000 : pageInput.size,
    sort: [sort.field, sort.direction].filter(Boolean).join(","),
  };

  const [manualState, setManualState] = useState<{
    data?: IPageable<T>;
    isPending: boolean;
    error?: any;
  }>({ isPending: true });
  const [manualKey, setManualKey] = useState(0);
  const requestIdRef = useRef(0);
  const queryKey = JSON.stringify(queryParams);

  // Fetch only when the page, filters or refresh signal change. Calling the
  // fetcher during render sent a request on every re-render of the table,
  // which server-paged tables cannot afford.
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    // A slow response for an older page or search must not replace a newer one.
    const isLatest = () => requestId === requestIdRef.current;
    setManualState((prev) => ({ ...prev, isPending: true }));
    Promise.resolve()
      .then(() => callFetchFunc(fetchFunc, queryParams))
      .then((data: IPageable<T>) => {
        if (isLatest()) setManualState({ data, isPending: false });
      })
      .catch((error: any) => {
        if (isLatest()) setManualState({ error, isPending: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualKey, queryKey, refreshSignal, fetchFunc]);

  const { data: pageable, isPending, error } = manualState;
  const handleRetry = () => setManualKey((k) => k + 1);
  const columnCount =
    fields.length + (actions.length ? 1 : 0) + (checkboxSelect ? 1 : 0);
  const effectivePending = Boolean(isPending) || Boolean(externalLoading);
  const rawContent: T[] = Array.isArray(pageable?.content) ? pageable!.content : [];

  const computedTotalElements = clientSidePagination
    ? rawContent.length
    : pageable?.totalElements ?? rawContent.length;

  const computedTotalPages = clientSidePagination
    ? Math.max(1, Math.ceil(computedTotalElements / pageInput.size))
    : pageable?.totalPages ??
      Math.max(1, Math.ceil(computedTotalElements / pageInput.size));

  const displayedContent = clientSidePagination
    ? rawContent.slice(
        pageInput.page * pageInput.size,
        (pageInput.page + 1) * pageInput.size
      )
    : rawContent;

  const [lastContent, setLastContent] = useState<T[] | null>(
    displayedContent && displayedContent.length > 0 ? displayedContent : null
  );

  useEffect(() => {
    if (!isPending && Array.isArray(rawContent) && rawContent.length >= 0) {
      setLastContent(rawContent.slice());
    }
  }, [JSON.stringify(rawContent), isPending]);

  const effectiveContent = effectivePending
    ? lastContent ?? displayedContent
    : displayedContent;

  const effectiveTotalElements = computedTotalElements;

  const effectiveTotalPages = clientSidePagination
    ? Math.max(1, Math.ceil(effectiveTotalElements / pageInput.size))
    : pageable?.totalPages ??
      Math.max(1, Math.ceil(effectiveTotalElements / pageInput.size));

  const highlights = highlightDiff
    ? (effectiveContent as any[]).map((elem: any, idx: number, list: any[]) => {
        const ref = list[idx + 1] ?? elem;
        return getObjectDiffMask(elem, ref);
      })
    : [];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (
      !clientSidePagination &&
      pageable &&
      Array.isArray(pageable.content) &&
      pageable.content.length === 0 &&
      pageInput.page > 0
    ) {
      setPageInput((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  }, [pageable, pageInput.page]);
  useEffect(() => {
    if (clientSidePagination) {
      const maxPage = Math.max(0, computedTotalPages - 1);
      if (pageInput.page > maxPage) {
        setPageInput((prev) => ({ ...prev, page: maxPage }));
      }
    }
  }, [clientSidePagination, computedTotalPages, pageInput.page]);

  const handleSort = (key: keyof T) => {
    setSort((prevSort) => {
      const newDirection =
        prevSort.field === key && prevSort.direction === "desc"
          ? "asc"
          : "desc";
      return { field: key, direction: newDirection };
    });
  };

  const getSortSymbol = (key: keyof T): ReactElement | undefined => {
    if (sort.field !== key) return undefined;
    return sort.direction === "asc" ? (
      <ArrowUpward sx={{ fontSize: 15 }} />
    ) : (
      <ArrowDownward sx={{ fontSize: 15 }} />
    );
  };

  const allSelected =
    checkboxSelect && isRowSelected
      ? (effectiveContent ?? []).every((row: T) => isRowSelected(row))
      : false;
  const handleSelectAllToggle = () => {
    if (onRowSelect) {
      (effectiveContent ?? []).forEach((row: T) =>
        onRowSelect(row, !allSelected)
      );
    }
  };
  const estimatedMinWidth = Math.max(
    700,
    fields.length * 120 + (actions && actions.length ? 80 : 0)
  );

  const phoneCards = (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "10px 10px 0 0",
        position: "relative",
        minHeight: 120,
      }}
    >
      {error ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 5, px: 2, textAlign: "center" }}>
          <ErrorOutlineIcon color="error" />
          <Typography sx={{ fontWeight: 600 }}>Couldn't load this data</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            sx={{ textTransform: "none", borderRadius: 2, minHeight: 40 }}
          >
            Retry
          </Button>
        </Box>
      ) : effectivePending &&
        (effectiveContent == null || effectiveContent.length === 0) ? (
        <Box aria-busy="true" sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 1.25 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" animation="wave" height={110} sx={{ borderRadius: 2.5 }} />
          ))}
        </Box>
      ) : (
        <>
          <DataCardList<T>
            fields={fields}
            content={effectiveContent ?? []}
            renderActions={renderActions}
            shadeRow={shadeRow}
            shadeBg={shadeBg}
            checkboxSelect={checkboxSelect}
            onRowSelect={onRowSelect}
            isRowSelected={isRowSelected}
            onRowClick={onRowClick}
          />
          {effectivePending && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.6)",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={36} thickness={5} />
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box>
      {isPhone ? phoneCards : (
      <TableContainer
        sx={{
          backgroundColor: "#fff",
          borderRadius: "10px 10px 0 0",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          position: "relative",
          width: "100%",
        }}
      >
        <Table sx={{ minWidth: estimatedMinWidth }}>
          <colgroup>
            {fields.map((f) => (
              <col key={String(f.key)} style={{ width: "auto" }} />
            ))}
            {actions.length ? <col style={{ width: "80px" }} /> : null}
          </colgroup>
          <TableHead>
            <TableRow>
              {checkboxSelect && (
                <TableCell sx={{ width: 50 }}>
                  <CustomCheckbox
                    checked={allSelected}
                    onChange={handleSelectAllToggle}
                    ariaLabel="select all rows"
                  />
                </TableCell>
              )}
              {fields.map((f) => {
                const isSortable = sortableFields?.includes(f.key);
                return (
                  <TableCell
                    key={String(f.key)}
                    onClick={isSortable ? () => handleSort(f.key) : undefined}
                    align="center"
                    sx={{
                      cursor: isSortable ? "pointer" : "default",
                      fontWeight: "600",
                      color: COLORS.typographyColor,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: "max-content", margin: "0 auto" }}
                      gap={1}
                    >
                      {f.header}
                      {isSortable && getSortSymbol(f.key)}
                    </Box>
                  </TableCell>
                );
              })}
              {actions.length ? (
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  Actions
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>

          {error ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  align="center"
                  sx={{ py: 5, borderBottom: "none" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(211, 47, 47, 0.08)",
                        color: "error.main",
                      }}
                    >
                      <ErrorOutlineIcon />
                    </Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Couldn't load this data
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check your connection and try again.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleRetry}
                      sx={{ mt: 1, textTransform: "none", borderRadius: 2 }}
                    >
                      Retry
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : effectivePending &&
            (effectiveContent == null || effectiveContent.length === 0) ? (
            <TableBody aria-busy="true">
              {Array.from({ length: Math.min(pageInput.size, 6) }).map(
                (_, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      height: 44,
                      backgroundColor:
                        rowIndex % 2 === 0
                          ? theme.palette.grey[100]
                          : theme.palette.common.white,
                    }}
                  >
                    {Array.from({ length: columnCount }).map((__, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{ borderBottom: "none", py: 1 }}
                      >
                        <Skeleton
                          variant="rounded"
                          animation="wave"
                          height={14}
                          sx={{
                            mx: "auto",
                            width: `${55 + ((rowIndex * 7 + colIndex * 13) % 35)}%`,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )}
            </TableBody>
          ) : (
            <>
              <DataTableBody<T>
                fields={fields}
                content={effectiveContent ?? []}
                actions={actions}
                renderActions={renderActions}
                shadeRow={shadeRow}
                shadeBg={shadeBg}
                shadeColor={shadeColor}
                checkboxSelect={checkboxSelect}
                onRowSelect={onRowSelect}
                isRowSelected={isRowSelected}
                onRowClick={onRowClick}
                highlights={highlights}
              />
              {effectivePending &&
                effectiveContent != null &&
                effectiveContent.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.6)",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    <CircularProgress
                      size={40}
                      thickness={5}
                      sx={{ color: COLORS.MainColor }}
                    />
                  </Box>
                )}
            </>
          )}
        </Table>
      </TableContainer>
      )}
      {!effectivePending &&
        (pageable || clientSidePagination) &&
        typeof effectiveTotalElements === "number" &&
        effectiveTotalPages >= 1 && (
          <Box
            sx={{
              alignItems: "center",
              borderRadius: "0 0  10px 10px",
              padding: "8px 16px",
              backgroundColor: "#fff",
            }}
          >
            <PaginationRow
              pageInput={pageInput}
              setPageInput={setPageInput}
              paginationSummary={paginationSummary}
              page={{
                page: pageInput.page,
                size: pageInput.size,
                totalElements: effectiveTotalElements,
                totalPages: effectiveTotalPages,
              }}
            />
          </Box>
        )}
    </Box>
  );
}

function PaginationRow({
  pageInput,
  setPageInput,
  page,
  paginationSummary,
}: IPaginationRowProps) {
  const currentPage = pageInput.page ?? 0;
  const totalPages = page.totalPages || 1;
  const totalElements = page.totalElements;

  const [pageInputValue, setPageInputValue] = useState<string>(
    String(currentPage + 1)
  );

  useEffect(() => {
    setPageInputValue(String(currentPage + 1));
  }, [currentPage]);

  const handlePageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  const handlePageInputKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      handlePageInputSubmit();
    }
  };

  const handlePageInputBlur = () => {
    handlePageInputSubmit();
  };

  const handlePageInputSubmit = () => {
    const inputNumber = parseInt(pageInputValue, 10);

    if (isNaN(inputNumber) || inputNumber < 1) {
      setPageInputValue(String(currentPage + 1));
      return;
    }

    const targetPage = Math.min(Math.max(inputNumber - 1, 0), totalPages - 1);

    if (targetPage !== currentPage) {
      setPageInput((prev) => ({
        ...prev,
        page: targetPage,
      }));
    }

    setPageInputValue(String(targetPage + 1));
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection={{ xs: "column", sm: "row" }}
      gap={{ xs: 1, sm: 2 }}
      ml={{ xs: 0, sm: 1 }}
      mr={{ xs: 0, sm: 1 }}
      sx={{ overflowX: "auto", width: "100%", p: { xs: 0.5, sm: 0 } }}
    >
      <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
        <IconButton
          disabled={currentPage <= 0}
          onClick={() =>
            setPageInput((prev) => ({
              ...prev,
              page: Math.max(prev.page - 1, 0),
            }))
          }
          sx={{
            color: currentPage > 0 ? COLORS.placeHolderColor : "#ccc",
            fontSize: { xs: "16px", sm: "18px" },
            p: { xs: 0.5, sm: 1 },
          }}
        >
          <ArrowBackIosNew fontSize="small" />
        </IconButton>

        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "13px", sm: "16px" },
            color: COLORS.placeHolderColor,
            minWidth: { xs: 120, sm: 150 },
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "bold",
              color: COLORS.placeHolderColor,
            }}
          >
            Page
          </Typography>
          <TextField
            value={pageInputValue}
            onChange={handlePageInputChange}
            onKeyPress={handlePageInputKeyPress}
            onBlur={handlePageInputBlur}
            size="small"
            sx={{
              width: { xs: "40px", sm: "50px" },
              "& .MuiInputBase-root": {
                height: { xs: "24px", sm: "28px" },
                fontSize: { xs: "12px", sm: "14px" },
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
                padding: { xs: "2px 4px", sm: "4px 6px" },
                fontWeight: "bold",
                color: COLORS.placeHolderColor,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: COLORS.placeHolderColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.placeHolderColor,
                  borderWidth: "1px",
                },
              },
            }}
            inputProps={{
              min: 1,
              max: totalPages,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "bold",
              color: COLORS.placeHolderColor,
            }}
          >
            of {totalPages}
          </Typography>
        </Box>

        <IconButton
          disabled={currentPage + 1 >= totalPages}
          onClick={() =>
            setPageInput((prev) => ({
              ...prev,
              page: Math.min(prev.page + 1, totalPages - 1),
            }))
          }
          sx={{
            color:
              currentPage + 1 < totalPages ? COLORS.placeHolderColor : "#ccc",
            fontSize: { xs: "16px", sm: "18px" },
            p: { xs: 0.5, sm: 1 },
          }}
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
      >
        <Box
          sx={{
            fontSize: { xs: "12px", sm: "0.875rem" },
            color: COLORS.placeHolderColor,
          }}
        >
          Rows per page:
        </Box>
        <Select
          value={pageInput.size}
          onChange={(e) =>
            setPageInput((prev) => ({
              ...prev,
              size: Number(e.target.value),
              page: 0,
            }))
          }
          sx={{
            height: { xs: "28px", sm: "32px" },
            fontSize: { xs: "12px", sm: "0.875rem" },
            width: { xs: "100%", sm: "80px" },
            minWidth: { xs: "80px", sm: "80px" },
            "& .MuiSelect-select": {
              padding: { xs: "2px 6px", sm: "4px 8px" },
            },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={30}>30</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
      </Box>
      {paginationSummary && (
        <Box
          sx={{
            ml: { xs: 0, sm: "auto" },
            mt: { xs: 0.5, sm: 0 },
            mr: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "auto" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          {paginationSummary}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: { xs: "12px", sm: "0.875rem" },
          color: COLORS.placeHolderColor,
          ml: paginationSummary ? 0 : { xs: 0, sm: "auto" },
          mt: { xs: 0.5, sm: 0 },
          width: { xs: "100%", sm: "auto" },
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        {totalElements > 0 && `Total elements: ${totalElements}`}
      </Typography>
    </Box>
  );
}

export default PaginatedTable;
